import React, { Component } from 'react';
import { WithStyles, withStyles } from '@mui/styles';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { withTranslate } from '../../core/TenantProvider';
import Snackbar from '@mui/material/Snackbar';
import classNames from 'classnames';
import BankDetailsContainerStyles from './styles/BankDetailsContainer.style';
import { ReactComponent as BankDetailsIcon } from './assets/bankDetailsIcon.svg';
import { ReducerState } from "../../redux/modules";
import { ReactComponent as InfoIcon } from './assets/icon-tick-circle.svg';
import { ReactComponent as SVGTickIcon } from './assets/icon-tick-circle.svg';
import { ReactComponent as SVGCloseIcon } from './assets/icon-close.svg';
import { Dispatch, bindActionCreators } from 'redux';
import { action as BankDetailsActions } from '../../redux/modules/bankDetails';
import { connect } from 'react-redux';
import { Accordion, AccordionSummary, Typography, AccordionDetails } from '@mui/material';
import { ReactComponent as AccordionIcon } from './assets/icon-chevron-up.svg';
import { ReactComponent as CopyIcon } from './assets/icon-copy.svg';
import APCurrencyIcon from '../../components/atoms/APCurrencyIcon';
import APLoadingSpinner from '../../components/atoms/APLoadingSpinner';
import { AffiliateDetailsAttributes } from '../../core/types/AffiliateDetails';
import APSegmentedControl from '../../components/atoms/APSegmentedControl';
import APSelect from '../../components/atoms/APSelect';
import APAlertMessage from '../../components/atoms/APAlertMessage';
import { GetCountriesDataForDropdown, GetCountryObjectAsPerCountryCode } from '../../core/utils/GetCountriesData/GetCountriesData';
import { OUT_BANK_DETAILS_DEFAULT_LEGAL_ENTITY, LEGAL_ENTITY_OPTIONS } from '../../core/utils/Constants/Constants';
import { EURO_CURRENCY_OPTION } from '../../core/utils/GetCurrenciesData/GetCurrenciesData';

interface BankDetailsContainerProps extends WithStyles<typeof BankDetailsContainerStyles> {
    translate: Function,
    getBankDetails: Function,
    getCountryDetails: Function,
    getCurrencyDetails: Function,
    fetchOrganizationBankDetails: Function,
    bankDetails: any,
    countryDetails: any,
    currencyDetails: any,
    organizationBankDetails: any,
    affiliateDetails: AffiliateDetailsAttributes | null,
    loadingState: string,
    getCurrencyDetailsLoadingState: string,
    organizationBankDetailsLoadingState: string,
    forTestingCall?: boolean,
}

interface BankDetailsContainerState {
    currentExpandedPanel: string,
    expandedCurrencyPanel: string | boolean,
    activeTab: string,
    selectedLegalEntity: string,
    selectedCountry: string,
    isCountryBlacklisted: boolean,
    copySuccess: boolean,
    noCountrySelectedSnackbar: boolean,
    bankDetailsNotFoundSnackbar: boolean,
    fetchedBankDetailsCache: Set<string>, // Cache fetched currency+country combinations
}

const OUR_BANK_DETAILS = 'ourBankDetails';
const YOUR_ACCOUNT = 'yourAccount';

class BankDetailsContainer extends Component<BankDetailsContainerProps, BankDetailsContainerState> {

    constructor(props) {
        super(props);
        this.state = {
            currentExpandedPanel: 'bank-details-panel-0',
            activeTab: YOUR_ACCOUNT,
            selectedLegalEntity: OUT_BANK_DETAILS_DEFAULT_LEGAL_ENTITY,
            selectedCountry: '',
            isCountryBlacklisted: false,
            expandedCurrencyPanel: false,
            copySuccess: false,
            noCountrySelectedSnackbar: false,
            bankDetailsNotFoundSnackbar: false,
            fetchedBankDetailsCache: new Set<string>(),
        }
        this.handleTabChange = this.handleTabChange.bind(this);
        this.handleCountryChange = this.handleCountryChange.bind(this);
        this.handleLegalEntityChange = this.handleLegalEntityChange.bind(this);
        this.handleCurrencyAccordionChange = this.handleCurrencyAccordionChange.bind(this);
        this.handleClose = this.handleClose.bind(this);
        this.handleCopySuccess = this.handleCopySuccess.bind(this);
    }
    
    setDefaultCountry() {
        const { countryDetails, affiliateDetails } = this.props;
        const { selectedCountry } = this.state;

        if (!selectedCountry && affiliateDetails?.affiliate?.country) {
            const affiliateCountry = affiliateDetails.affiliate.country;
            const countryOptions = GetCountriesDataForDropdown()?.options || [];

            // Try to find by value (ISO code) or label (Country Name)
            const matchedOption = countryOptions.find(opt => 
                opt.value.toLowerCase() === affiliateCountry.toLowerCase() || 
                opt.label.toLowerCase() === affiliateCountry.toLowerCase()
            );

            if (matchedOption) {
                const countryCode = matchedOption.value;
                const countryObject = GetCountryObjectAsPerCountryCode(countryCode, countryDetails);
                const isCountryBlacklisted = !!(countryObject && countryObject.blackListed === 'ACTIVE');

                this.setState({
                    selectedCountry: countryCode,
                    isCountryBlacklisted
                });
            }
        }
    }


    componentDidMount() {
        const { getBankDetails, getCountryDetails, getCurrencyDetails, affiliateDetails, 
                countryDetails, currencyDetails, forTestingCall = false } = this.props;
        !forTestingCall && getBankDetails(affiliateDetails?.affiliateProfileId);
        // Only fetch country/currency details if not already loaded (performance optimization)
        !forTestingCall && !countryDetails && getCountryDetails();
        !forTestingCall && !currencyDetails && getCurrencyDetails();
        
        this.setDefaultCountry();
    }

    componentDidUpdate(prevProps: BankDetailsContainerProps) {
        const { organizationBankDetailsLoadingState } = this.props;

        // Check if API call failed
        if (
            prevProps.organizationBankDetailsLoadingState !== 'ERROR' &&
            organizationBankDetailsLoadingState === 'ERROR'
        ) {
            this.setState({ 
                bankDetailsNotFoundSnackbar: true,
                expandedCurrencyPanel: false // Collapse accordion on error
            });
        }

        this.setDefaultCountry();
    }

    handleChange(panel: string) {
        return ((event: React.ChangeEvent<{}>, isExpanded: boolean) => {
            this.setState({
                currentExpandedPanel: isExpanded ? panel : ''
            });
        });
    }

    handleTabChange(value: string) {
        this.setState({ activeTab: value });
    }

    handleCountryChange(evt: any) {
        const { organizationBankDetailsLoadingState } = this.props;
        if (organizationBankDetailsLoadingState === 'LOADING') {
            return;
        }

        const { target: { value } } = evt;
        const { countryDetails } = this.props;
        const countryObject = GetCountryObjectAsPerCountryCode(value, countryDetails);
        const isCountryBlacklisted = !!(countryObject && countryObject.blackListed === 'ACTIVE');
        this.setState({ 
            selectedCountry: value, 
            isCountryBlacklisted,
            expandedCurrencyPanel: false, 
            fetchedBankDetailsCache: new Set<string>(),
        });
    }

    handleLegalEntityChange(evt: any) {
        const { target: { value } } = evt;

        this.setState({
            selectedLegalEntity: value,
            expandedCurrencyPanel: false,
            fetchedBankDetailsCache: new Set<string>(),
        });
    }

    handleCurrencyAccordionChange(panel: string, currency: string) {
        return ((event: React.ChangeEvent<{}>, isExpanded: boolean) => {
            const { fetchOrganizationBankDetails } = this.props;
            const { selectedCountry, selectedLegalEntity, fetchedBankDetailsCache } = this.state;

            if (isExpanded && !selectedCountry) {
                event.preventDefault();
                this.setState({ noCountrySelectedSnackbar: true });
                return;
            }

            if (isExpanded && selectedCountry) {
                const cacheKey = `${currency}_${selectedCountry}_${selectedLegalEntity}`;
                
                if (!fetchedBankDetailsCache.has(cacheKey)) {
                    const payload = {
                        currency: currency,
                        affiliateLegalEntity: selectedLegalEntity,
                        countryIsoCode: selectedCountry
                    };
                    
                    fetchOrganizationBankDetails(payload).then(() => {
                        this.setState(prevState => ({
                            fetchedBankDetailsCache: new Set(prevState.fetchedBankDetailsCache).add(cacheKey)
                        }));
                    }).catch(() => {
                        // Error is handled in componentDidUpdate
                    });
                }
            }

            this.setState({
                expandedCurrencyPanel: isExpanded ? panel : false
            });
        });
    }

    handleClose(e?: React.SyntheticEvent<any, Event> | Event, reason?: string) {
        if (reason === 'clickaway') {
            return;
        }
        this.setState({ 
            copySuccess: false, 
            noCountrySelectedSnackbar: false,
            bankDetailsNotFoundSnackbar: false
        });
    }

    handleCopySuccess() {
        this.setState({ copySuccess: true });
    }

    render() {
        const {
            classes,
            translate,
            bankDetails,
            affiliateDetails,
            loadingState,
            organizationBankDetailsLoadingState,
        } = this.props;

        const isOrganizationBankDetailsLoading =
            organizationBankDetailsLoadingState === 'LOADING';

        const {
            activeTab,
            selectedLegalEntity,
            selectedCountry,
            isCountryBlacklisted,
            expandedCurrencyPanel,
            copySuccess,
            noCountrySelectedSnackbar,
            bankDetailsNotFoundSnackbar,
        } = this.state;

        const tabs = [
            { label: translate('bank.details.tabs.your'), value: YOUR_ACCOUNT },
            { label: translate('bank.details.tabs.our'), value: OUR_BANK_DETAILS }
        ];

        const isCdleuAffiliate = affiliateDetails?.affiliate?.legal_entity === 'CDLEU';
        const selectedTab = isCdleuAffiliate ? activeTab : YOUR_ACCOUNT;

        const currentBankAccountDetails = (bankDetails && bankDetails.length > 0) ? bankDetails : {};

        const SettingPageNoBankTranslation: () => JSX.Element = () => {
            return (<p>
                {translate("bank.no.details.footer.line1")}
                <a className={classes.footerTextlink} href={"/settings"}>
                    {translate("bank.no.details.footer.line1.setting")}
                </a>{translate("bank.no.details.footer.line2")}
            </p>);
        }

        const SettingPageBankTranslation: () => JSX.Element = () => {
            return (<p>
                {translate("bank.details.footer.line1")}
                <a className={classes.footerTextlink} href={"/settings"}>
                    {translate("bank.details.footer.line1.setting")}
                </a>{translate("bank.details.footer.line1.alternatively")}
            </p>);
        }

        const emptyBankDetailsView = (
            <div className={classes.emptyBankDetailsContainer}>
                <h6>{translate('bank.no.details.header')}</h6>
                <div className={classes.emptybankDetailsOuterSection}>
                    <div className={classes.emptybankDetailsInnerSection}>
                        <div className={classes.IconWrapper}>
                            <BankDetailsIcon />
                            <i>
                                <InfoIcon />
                            </i>
                        </div>
                        <div className={classes.emptybankDetailsFooterSection}>
                            {SettingPageNoBankTranslation()}
                            <p>
                                <span>{affiliateDetails?.affiliate?.owner_emailid}</span>
                            </p>
                            <p>{translate('bank.no.details.footer.line3', {
                                country_code: affiliateDetails?.affiliate?.owner_country_isdcode,
                                phone_number: affiliateDetails?.affiliate?.owner_phone_no
                            })}</p>
                        </div>
                    </div>
                </div>
            </div>);

        const bankDetailsSingleAccount =
            (
                <Accordion className={classes.accordionSection}
                    expanded
                    id={`bank-details-panel-0`} >
                    <AccordionSummary
                        aria-controls={`bank-details-0`}
                        id={`bank-details-0-header`}>
                        <Typography><APCurrencyIcon currency={currentBankAccountDetails[0]?.PaymentCurrency} />{currentBankAccountDetails[0]?.AccountName}</Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.accordionDetails}>
                        <div className={classes.bankDetailsOuterSection}>
                            <div className={classes.bankDetailsInnerSection}>
                                <ul>
                                    <li>{translate('bank.details.account.name')}</li>
                                    <li>{currentBankAccountDetails[0]?.AccountName}</li>
                                    <li>{translate('bank.details.bank.name')}</li>
                                    <li>{currentBankAccountDetails[0]?.BankName}</li>
                                    <li>{translate('bank.details.sort.code')}</li>
                                    <li>{(currentBankAccountDetails[0]?.Sort_Code ? currentBankAccountDetails[0]?.Sort_Code : '')}</li>
                                    <li>{translate('bank.details.account.no')}</li>
                                    <li>{currentBankAccountDetails[0]?.AccountNo}</li>
                                    <li>{translate('bank.details.swift.code')}</li>
                                    <li>{currentBankAccountDetails[0]?.SwiftCode}</li>
                                </ul>
                            </div>
                        </div>
                    </AccordionDetails>
                </Accordion>
            );


        const bankDetailsAccordion = currentBankAccountDetails && currentBankAccountDetails.length > 1 &&
            currentBankAccountDetails.map((currentBankDetails, index) => {
                return (
                    <Accordion
                        key={index}
                        className={classes.accordionSection}
                        defaultExpanded={index === 0}
                        expanded={this.state.currentExpandedPanel === ('bank-details-panel-' + index)}
                        onChange={this.handleChange(('bank-details-panel-' + index))}
                        id={`bank-details-panel-${index}`} >

                        <AccordionSummary
                            expandIcon={<AccordionIcon />}
                            aria-controls={`bank-details-${index}`}
                            id={`bank-details-${index}-header`}>
                            <Typography><APCurrencyIcon currency={currentBankDetails.PaymentCurrency} />{currentBankDetails?.AccountName}</Typography>
                        </AccordionSummary>
                        <AccordionDetails className={classes.accordionDetails}>
                            <div className={classes.bankDetailsOuterSection}>
                                <div className={classes.bankDetailsInnerSection}>
                                    <ul>
                                        <li>{translate('bank.details.account.name')}</li>
                                        <li>{currentBankDetails?.AccountName}</li>
                                        <li>{translate('bank.details.bank.name')}</li>
                                        <li>{currentBankDetails?.BankName}</li>
                                        <li>{translate('bank.details.sort.code')}</li>
                                        <li>{(currentBankDetails?.Sort_Code ? currentBankDetails?.Sort_Code : '')}</li>
                                        <li>{translate('bank.details.account.no')}</li>
                                        <li>{currentBankDetails?.AccountNo}</li>
                                        <li>{translate('bank.details.swift.code')}</li>
                                        <li>{currentBankDetails?.SwiftCode}</li>
                                    </ul>
                                </div>
                            </div>
                        </AccordionDetails>
                    </Accordion>
                )
            });

        const bankDetailsView = (
            <div className={classes.bankDetailsContainer}>
                <h6>{translate('bank.details.header')}</h6>
                {(currentBankAccountDetails.length > 1) ? (bankDetailsAccordion) : (bankDetailsSingleAccount)}
                <div className={classes.bankDetailsFooterSection}>
                    {SettingPageBankTranslation()}
                    <p>{translate('bank.details.footer.line2', {
                        owner_first_name: affiliateDetails?.affiliate?.owner_first_name,
                        owner_last_name: affiliateDetails?.affiliate?.owner_last_name
                    })}
                        <span>{affiliateDetails?.affiliate?.owner_emailid}</span>
                    </p>
                    <p>{translate('bank.details.footer.line3', {
                        country_code: affiliateDetails?.affiliate?.owner_country_isdcode,
                        phone_number: affiliateDetails?.affiliate?.owner_phone_no
                    })}</p>
                </div>
            </div>

        );

        const ourBankDetailsView = (
            <div className={classes.ourBankDetailsContainer}>
                <h2 className={classes.ourBankDetailsHeader}>{translate('bank.details.our.header')}</h2>
                <p className={classes.ourBankDetailsDescription}>
                    {translate('bank.details.our.description')}
                </p>
                <APSelect
                    className={classes.countrySelect}
                    name="country"
                    label={translate('bank.details.our.country.label')}
                    value={selectedCountry}
                    onChange={this.handleCountryChange}
                    type="country"
                    data={GetCountriesDataForDropdown()?.options || []}
                    disabled={isOrganizationBankDetailsLoading}
                />
                <APSelect
                    className={classes.legalEntitySelect}
                    name="legalEntity"
                    label={translate('bank.details.our.legalEntity.label')}
                    value={selectedLegalEntity}
                    onChange={this.handleLegalEntityChange}
                    data={LEGAL_ENTITY_OPTIONS}
                    disableClearValue
                    disabled={isOrganizationBankDetailsLoading}
                />
                {isCountryBlacklisted && (
                    <APAlertMessage
                        className={classes.blacklistAlert}
                        message={translate("bank.account.lookup.bankdetails.alert.message")}
                        title={translate("bank.account.lookup.bankdetails.alert.title")}
                        severity={"error"}
                        icon={'error'}
                        hideCloseIcon={true}
                    />
                )}
            </div>
        );

        const currencyListView = !isCountryBlacklisted && (
            <div className={classes.currencyListContainer}>
                    <Accordion
                        expanded={expandedCurrencyPanel === `currency-panel`}
                        onChange={this.handleCurrencyAccordionChange(`currency-panel`, EURO_CURRENCY_OPTION.value)}
                        className={classes.accordion}
                    >
                        <AccordionSummary
                            expandIcon={<AccordionIcon className={classes.expandIcon} />}
                            aria-controls={`currency-panel-content`}
                            id={`currency-panel-header`}
                            className={classes.accordionSummary}
                        >
                            <div className={classes.currencyContainer}>
                                <APCurrencyIcon currency={EURO_CURRENCY_OPTION.value} />
                                <Typography className={classes.currencyCode}>{EURO_CURRENCY_OPTION.value}</Typography>
                            </div>
                        </AccordionSummary>
                        <AccordionDetails className={classes.accordionDetails}>
                            {isOrganizationBankDetailsLoading ? (
                                <div className={classes.loaderWrapper}><APLoadingSpinner /> </div>
                            ) : (
                                <div className={classes.detailRow}>
                                    <ul className={classes.detailsList}>
                                        <li className={classes.detailItem}>
                                            <div className={classes.detailTextContainer}>
                                                <Typography className={classes.detailLabel}>{translate('bank.details.our.currency.account.name')}</Typography>
                                                <Typography className={classes.detailValue}>{this.props.organizationBankDetails?.primaryAccountName || '-'}</Typography>
                                            </div>
                                            <CopyToClipboard text={this.props.organizationBankDetails?.primaryAccountName || ''} onCopy={this.handleCopySuccess}>
                                                <div className={classes.copyIcon}>
                                                    <CopyIcon />
                                                </div>
                                            </CopyToClipboard>
                                        </li>
                                        <li className={classes.detailItem}>
                                            <div className={classes.detailTextContainer}>
                                                <Typography className={classes.detailLabel}>{translate('bank.details.our.currency.account.address')}</Typography>
                                                <Typography className={classes.detailValue}>{this.props.organizationBankDetails?.primaryAccountAddress || '-'}</Typography>
                                            </div>
                                            <CopyToClipboard text={this.props.organizationBankDetails?.primaryAccountAddress || ''} onCopy={this.handleCopySuccess}>
                                                <div className={classes.copyIcon}>
                                                    <CopyIcon />
                                                </div>
                                            </CopyToClipboard>
                                        </li>
                                        <li className={classes.detailItem}>
                                            <div className={classes.detailTextContainer}>
                                                <Typography className={classes.detailLabel}>{translate('bank.details.our.currency.bank.name')}</Typography>
                                                <Typography className={classes.detailValue}>{this.props.organizationBankDetails?.primaryBankName || '-'}</Typography>
                                            </div>
                                            <CopyToClipboard text={this.props.organizationBankDetails?.primaryBankName || ''} onCopy={this.handleCopySuccess}>
                                                <div className={classes.copyIcon}>
                                                    <CopyIcon />
                                                </div>
                                            </CopyToClipboard>
                                        </li>
                                        <li className={classes.detailItem}>
                                            <div className={classes.detailTextContainer}>
                                                <Typography className={classes.detailLabel}>{translate('bank.details.our.currency.bank.address')}</Typography>
                                                <Typography className={classes.detailValue}>{this.props.organizationBankDetails?.primaryBankAddress || '-'}</Typography>
                                            </div>
                                            <CopyToClipboard text={this.props.organizationBankDetails?.primaryBankAddress || ''} onCopy={this.handleCopySuccess}>
                                                <div className={classes.copyIcon}>
                                                    <CopyIcon />
                                                </div>
                                            </CopyToClipboard>
                                        </li>
                                        <li className={classes.detailItem}>
                                            <div className={classes.detailTextContainer}>
                                                <Typography className={classes.detailLabel}>{translate('bank.details.our.currency.bank.code.number.1')}</Typography>
                                                <Typography className={classes.detailValue}>{this.props.organizationBankDetails?.primaryBankCodeNumber1 || '-'}</Typography>
                                            </div>
                                            <CopyToClipboard text={this.props.organizationBankDetails?.primaryBankCodeNumber1 || ''} onCopy={this.handleCopySuccess}>
                                                <div className={classes.copyIcon}>
                                                    <CopyIcon />
                                                </div>
                                            </CopyToClipboard>
                                        </li>
                                        <li className={classes.detailItem}>
                                            <div className={classes.detailTextContainer}>
                                                <Typography className={classes.detailLabel}>{translate('bank.details.our.currency.bank.code.number.2')}</Typography>
                                                <Typography className={classes.detailValue}>{this.props.organizationBankDetails?.primaryBankCodeNumber2 || '-'}</Typography>
                                            </div>
                                            <CopyToClipboard text={this.props.organizationBankDetails?.primaryBankCodeNumber2 || ''} onCopy={this.handleCopySuccess}>
                                                <div className={classes.copyIcon}>
                                                    <CopyIcon />
                                                </div>
                                            </CopyToClipboard>
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </AccordionDetails>
                    </Accordion>
            </div>
        );

        const getActiveTabLabel = () => {
            const activeTabObj = tabs.find(tab => tab.value === selectedTab);
            return activeTabObj ? activeTabObj.label : '';
        };

        return (
            <div className={classes.bankDetailContainer}>
                {copySuccess && (
                     <Snackbar
                        className={classNames(classes.APSnackBar, classes.snackBarWidth)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        open={copySuccess}
                        autoHideDuration={3000}
                        onClose={this.handleClose}
                    >
                        <div>
                            <SVGTickIcon className={classes.svgTick} />
                            {translate('trackingCode.copycode.success')}
                            <SVGCloseIcon
                                className={classes.svgIconClose}
                                onClick={this.handleClose}
                            />
                        </div>
                    </Snackbar>
                )}
                {noCountrySelectedSnackbar && (
                     <Snackbar
                        className={classNames(classes.APSnackBar, classes.snackBarWidth, classes.errorSnackBar)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        open={noCountrySelectedSnackbar}
                        autoHideDuration={3000}
                        onClose={this.handleClose}
                    >
                        <div>
                            {translate('bank.details.error.no.country.selected')}
                            <SVGCloseIcon
                                className={classes.svgIconClose}
                                onClick={this.handleClose}
                            />
                        </div>
                    </Snackbar>
                )}
                {bankDetailsNotFoundSnackbar && (
                     <Snackbar
                        className={classNames(classes.APSnackBar, classes.snackBarWidth, classes.errorSnackBar)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        open={bankDetailsNotFoundSnackbar}
                        autoHideDuration={3000}
                        onClose={this.handleClose}
                    >
                        <div>
                            {translate('bank.details.error.not.found')}
                            <SVGCloseIcon
                                className={classes.svgIconClose}
                                onClick={this.handleClose}
                            />
                        </div>
                    </Snackbar>
                )}
                {isCdleuAffiliate && <h1 className={classes.pageHeader}>{getActiveTabLabel()}</h1>}
                {isCdleuAffiliate && (
                    <div className={classes.fullWidth}>
                        <APSegmentedControl
                            onChange={this.handleTabChange}
                            options={tabs}
                            selection={activeTab}
                        />
                    </div>
                )}
                {(loadingState === 'LOADING' || loadingState === 'UNINITIALIZED') ?
                    <div className={classes.loaderWrapper}><APLoadingSpinner /> </div> : 
                    selectedTab === YOUR_ACCOUNT ? 
                        ((bankDetails && bankDetails.length > 0) ? (bankDetailsView) : (emptyBankDetailsView)) : 
                        (
                            <div>
                                {ourBankDetailsView}
                                {currencyListView}
                            </div>
                        )
                }
            </div>
        );
    }
}

const mapStateToProps = (store: ReducerState) => ({
    affiliateDetails: store.user.affiliateDetails,
    bankDetails: store.bankDetails.bankDetails,
    countryDetails: store.bankDetails.getCountryDetails,
    currencyDetails: store.bankDetails.getCurrencyDetails,
    loadingState: store.bankDetails.loadingState,
    getCurrencyDetailsLoadingState: store.bankDetails.getCurrencyDetailsLoadingState,
    organizationBankDetails: store.bankDetails.organizationBankDetails,
    organizationBankDetailsLoadingState: store.bankDetails.organizationBankDetailsLoadingState,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
    getBankDetails: BankDetailsActions.getBankDetails,
    getCountryDetails: BankDetailsActions.getCountryDetails,
    getCurrencyDetails: BankDetailsActions.getCurrencyDetails,
    fetchOrganizationBankDetails: BankDetailsActions.fetchOrganizationBankDetails,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(
    withTranslate(withStyles(BankDetailsContainerStyles)(
        (BankDetailsContainer))
    )
);